import React, {useCallback} from 'react';


import {BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {
    disableLcdSubscriptionsProgress,
    disableLcdSubscriptionsResult,
} from 'src/openapi/admin/apis/BulkLcdSubscriptionDisableApi';
import {useAppDispatch} from 'src/store/hooks';

import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {LCS_SUBSCRIPTION_TITLES} from '../constants';
import {UpdateLcdResultDescription} from '../update-lcd-result-description';

import type {BulkActionResult} from 'src/components/bulk-tool/typedef';
import type {LcdEnabledItem} from 'src/openapi/admin/models/LcdEnabledItem';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';

type Result = {
    success: true,
    error: false,
    data: BulkActionResult<LcdEnabledItem, {[key: string]: Array<RegionSettings>}>,
};

type Props = {
    id: string,
    onClose: () => void,
};

export const UnsubscribeFromLcdCreditAnalysisResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: LcdEnabledItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: LcdEnabledItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.userEmail}
                            blank={true}
                            place="Bulk Unsubscribe from LCD Credit Analysis Result"
                        />
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<LcdEnabledItem, {[key: string]: Array<RegionSettings>}>
            id={id}
            onClose={onClose}

            toolTitle={LCS_SUBSCRIPTION_TITLES.UNSUBSCRIBE}
            description={bulkResult => <UpdateLcdResultDescription bulkResult={bulkResult} subscribe={false}/>}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={400}>Email</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(disableLcdSubscriptionsProgress({id}))}
            getResult={async () => {
                const response = await apiDispatch(disableLcdSubscriptionsResult({id}));

                if (response.payload.success) {
                    const payload: Result = {
                        success: true,
                        error: false,
                        data: {
                            itemList: response.payload.data.itemList,
                            startTime: response.payload.data.startTime,
                            additionalData: response.payload.data.enabledRegionSettings,
                        },
                    };

                    return Promise.resolve({type: '', payload});
                } else {
                    return Promise.resolve({type: '', payload: response.payload});
                }
            }}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
